import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  DollarSign,
  ShoppingCart,
  Users,
  Package,
  TrendingUp,
  TrendingDown,
  Download,
  Calendar,
  FileText,
  FileJson,
} from 'lucide-react';
import {
  useTikTokFetch,
  AnalyticsOverview,
  formatCurrency,
  formatNumber,
  formatPercentage,
} from '../hooks/useTikTokFetch';
import '../styles/pages.css';

const RANGES = [
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
];

const EXPORT_FORMATS = [
  { label: 'CSV', value: 'csv', icon: FileText },
  { label: 'JSON', value: 'json', icon: FileJson },
] as const;

type ExportFormat = 'csv' | 'json';

export default function Analytics() {
  const { get, getShopId, fetch: fetchWithAuth } = useTikTokFetch();
  const shopId = getShopId();
  const [days, setDays] = useState(30);
  const [showExport, setShowExport] = useState(false);
  const [exportFormat, setExportFormat] = useState<ExportFormat>('csv');
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [exporting, setExporting] = useState(false);

  const { data: analytics, isLoading } = useQuery<AnalyticsOverview>({
    queryKey: ['analytics', shopId, days],
    queryFn: () => get(`/api/shop/${shopId}/analytics?days=${days}`),
    enabled: !!shopId,
  });

  const handleExport = useCallback(async () => {
    if (!shopId) return;
    setExporting(true);
    try {
      const params = new URLSearchParams({ format: exportFormat });
      if (exportStartDate) params.set('start_date', exportStartDate);
      if (exportEndDate) params.set('end_date', exportEndDate);

      const response = await fetchWithAuth(
        `/api/shop/${shopId}/analytics/export?${params}`
      );

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const ext = exportFormat === 'json' ? 'json' : 'csv';
      const filename = `analytics-export-${new Date().toISOString().slice(0, 10)}.${ext}`;

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setShowExport(false);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  }, [shopId, exportFormat, exportStartDate, exportEndDate, fetchWithAuth]);

  const stats = analytics
    ? [
        {
          label: 'Revenue',
          value: formatCurrency(analytics.total_revenue),
          trend: analytics.revenue_trend,
          icon: DollarSign,
        },
        {
          label: 'Orders',
          value: formatNumber(analytics.total_orders),
          trend: analytics.orders_trend,
          icon: ShoppingCart,
        },
        {
          label: 'Commission',
          value: formatCurrency(analytics.total_commission),
          trend: null,
          icon: TrendingUp,
        },
        {
          label: 'Active Creators',
          value: formatNumber(analytics.total_creators),
          trend: null,
          icon: Users,
        },
        {
          label: 'Products',
          value: formatNumber(analytics.total_products),
          trend: null,
          icon: Package,
        },
      ]
    : [];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Analytics</h1>
        <div className="header-actions">
          <div className="filter-row">
            {RANGES.map((r) => (
              <button
                key={r.value}
                className={`filter-btn ${days === r.value ? 'filter-btn--active' : ''}`}
                onClick={() => setDays(r.value)}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            className="btn btn--secondary"
            onClick={() => setShowExport(!showExport)}
          >
            <Download size={14} />
            Export
          </button>
        </div>
      </div>

      {/* Export Panel */}
      {showExport && (
        <motion.section
          className="card"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ marginBottom: '1.25rem' }}
        >
          <div className="card-header">
            <Download size={18} />
            <h2>Export Analytics</h2>
          </div>
          <div className="card-body">
            <div className="export-form">
              <div className="export-row">
                <div className="form-group">
                  <label className="form-label">Format</label>
                  <div className="filter-row">
                    {EXPORT_FORMATS.map((f) => (
                      <button
                        key={f.value}
                        className={`filter-btn ${exportFormat === f.value ? 'filter-btn--active' : ''}`}
                        onClick={() => setExportFormat(f.value)}
                      >
                        <f.icon size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <Calendar size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
                    Start Date
                  </label>
                  <input
                    type="date"
                    className="input"
                    value={exportStartDate}
                    onChange={(e) => setExportStartDate(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <Calendar size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
                    End Date
                  </label>
                  <input
                    type="date"
                    className="input"
                    value={exportEndDate}
                    onChange={(e) => setExportEndDate(e.target.value)}
                  />
                </div>
              </div>
              <div className="form-actions">
                <button
                  className="btn btn--primary"
                  onClick={handleExport}
                  disabled={exporting}
                >
                  <Download size={14} />
                  {exporting ? 'Exporting...' : `Download ${exportFormat.toUpperCase()}`}
                </button>
                <button className="btn btn--secondary" onClick={() => setShowExport(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </motion.section>
      )}

      {isLoading ? (
        <div className="loading-spinner" />
      ) : !analytics ? (
        <div className="empty-state"><p>No analytics data available</p></div>
      ) : (
        <>
          {/* Stat Cards */}
          <div className="stat-grid">
            {stats.map((s, i) => (
              <motion.div
                key={s.label}
                className="stat-card"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <div className="stat-icon"><s.icon size={18} /></div>
                <span className="stat-label">{s.label}</span>
                <span className="stat-value">{s.value}</span>
                {s.trend !== null && (
                  <span className={`stat-trend ${s.trend >= 0 ? 'trend--up' : 'trend--down'}`}>
                    {s.trend >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                    {formatPercentage(s.trend)}
                  </span>
                )}
              </motion.div>
            ))}
          </div>

          {/* Top Creators */}
          <motion.section
            className="card"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="card-header">
              <Users size={18} />
              <h2>Top Creators</h2>
            </div>
            <div className="card-body">
              {analytics.top_creators.length === 0 ? (
                <p className="text-muted">No creator data yet</p>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr><th>Creator</th><th>Orders</th><th>Revenue</th></tr>
                  </thead>
                  <tbody>
                    {analytics.top_creators.map((c) => (
                      <tr key={c.id}>
                        <td>{c.name}</td>
                        <td>{c.orders}</td>
                        <td className="text-accent">{formatCurrency(c.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </motion.section>

          {/* Top Products */}
          <motion.section
            className="card"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="card-header">
              <Package size={18} />
              <h2>Top Products</h2>
            </div>
            <div className="card-body">
              {analytics.top_products.length === 0 ? (
                <p className="text-muted">No product data yet</p>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr><th>Product</th><th>Views</th><th>Sales</th></tr>
                  </thead>
                  <tbody>
                    {analytics.top_products.map((p) => (
                      <tr key={p.id}>
                        <td>{p.title}</td>
                        <td>{formatNumber(p.views)}</td>
                        <td>{formatNumber(p.sales)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </motion.section>
        </>
      )}
    </div>
  );
}
